/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

interface CodeBlockProps {
    className?: string;
    code: string;
    language?: string;
    roundedTop?: boolean;
    theme: 'light' | 'dark';
    wrapperClassName?: string;
}

function CodeBlock({
    className = '',
    code,
    language = 'tsx',
    roundedTop = true,
    theme,
    wrapperClassName = '',
}: CodeBlockProps) {
    const { t } = useI18n();
    const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle');

    useEffect(() => {
        if (copyState !== 'copied') {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setCopyState('idle');
        }, 1500);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [copyState]);

    const handleCopy = async (): Promise<void> => {
        await navigator.clipboard.writeText(code);
        setCopyState('copied');
    };

    return (
        <div className={`layouts-code-block ${wrapperClassName}`.trim()}>
            <div className="action-controls layouts-code-block__copy rounded-box p-1">
                <div className="tooltip tooltip-left" data-tip={t(copyState === 'copied' ? 'Copied' : 'Copy code')}>
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm btn-square"
                        onClick={() => {
                            void handleCopy();
                        }}
                        aria-label={t(copyState === 'copied' ? 'Copied' : 'Copy code')}
                    >
                        {copyState === 'copied'
                            ? <Check className="h-4 w-4" aria-hidden="true" />
                            : <Copy className="h-4 w-4" aria-hidden="true" />}
                    </button>
                </div>
            </div>
            <SyntaxHighlighter
                language={language}
                style={theme === 'dark' ? oneDark : oneLight}
                wrapLongLines
                showLineNumbers
                className={`layouts-code ${className}`.trim()}
                customStyle={{
                    margin: 0,
                    padding: '1rem 1.25rem',
                    border: '1px solid color-mix(in srgb, var(--color-base-content) 12%, transparent)',
                    borderRadius: roundedTop
                        ? 'var(--app-surface-radius)'
                        : '0 var(--app-surface-radius) var(--app-surface-radius) var(--app-surface-radius)',
                    fontSize: '0.875rem',
                }}
                codeTagProps={{
                    style: {
                        fontFamily: [
                            'ui-monospace',
                            'SFMono-Regular',
                            'Menlo',
                            'Monaco',
                            'Consolas',
                            "'Liberation Mono'",
                            "'Courier New'",
                            'monospace',
                        ].join(', '),
                    },
                }}
                lineNumberStyle={{
                    minWidth: '2.75rem',
                    paddingRight: '1rem',
                    color: 'color-mix(in srgb, var(--color-base-content) 45%, transparent)',
                }}
            >
                {code}
            </SyntaxHighlighter>
        </div>
    );
}

export default CodeBlock;
